import { forwardRef } from 'react'

import type { DraggableProps, DraggableProvided } from 'react-beautiful-dnd'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faToggleOff, faToggleOn } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Form, Link, ListItem,
} from '@domain-group/suit-theme'

import useProviderPriceListItemOptionValue, {
  useProviderPriceListItemOptionValueForm,
} from '@hooks/useProviderPriceListItemOptionValue'

import DeleteIcon from '@components/PriceListManager/components/DeleteIcon'

import { confirmDelete } from '@functions/entity'

type DefaultFormattedParams = {
  isFirstItem: boolean,
  isMaster: boolean,
}

const DefaultFormatted = (params: DefaultFormattedParams) => {
  const { isFirstItem, isMaster } = params
  if (isFirstItem && isMaster){
    return ' '
  }

  if (isFirstItem){
    return 'Visible'
  }

  return ''
}

type PriceTextFormattedParams = {
  isFirstItem: boolean,
  isMaster: boolean,
}

const priceTextFormatted = (params: PriceTextFormattedParams) => {
  const { isFirstItem, isMaster } = params
  if (isFirstItem && isMaster){
    return '* Default Price'
  }

  if (isFirstItem){
    return '* Price'
  }

  return ''
}

interface DraggableProviderPriceListOptionValueProps {
  draggableProps?: DraggableProps,
  dragHandleProps?: DraggableProvided['dragHandleProps'],
  dragSnapshot?: {
    isDragging: boolean,
  },
  isMaster: boolean,
  isFirstItem: boolean,
  providerPriceListItemOptionValueId: number,
}

const DraggableProviderPriceListOptionValue = forwardRef((props: DraggableProviderPriceListOptionValueProps, ref) => {
  const {
    dragHandleProps, dragSnapshot, draggableProps, isFirstItem, isMaster, providerPriceListItemOptionValueId,
  } = props

  const { isDragging } = dragSnapshot || {}

  const priceListItemOptionValuePayload = useProviderPriceListItemOptionValue({
    id: providerPriceListItemOptionValueId,
  })
  const {
    callbacks: {
      deleteProviderPriceListItemOptionValue: deleteFn,
      toggleDefault: toggleDefaultFn,
      toggleVisibility: toggleVisibilityFn,
      updateProviderPriceListItemOptionValue: updateFn,
    },
    providerPriceListItemOptionValue,
  } = priceListItemOptionValuePayload

  const { hidden, is_default: isDefault } = providerPriceListItemOptionValue

  const priceListItemOptionValueFormPayload = useProviderPriceListItemOptionValueForm(providerPriceListItemOptionValue)
  const {
    entityState,
    entityState: {
      price,
      product_code,
      title,
    },
    handlers,
  } = priceListItemOptionValueFormPayload

  const confirmDeletePayload = {
    callbacks: {
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Item Option Value',
    },
  }

  return (
    <ListItem
      boxProps={{
        backgroundColor: 'white',
        paddingY: 'medium',
        justifyContent: 'space-between',
      }}
      disableHover
      forwardProps={{
        ...draggableProps,
        ...dragHandleProps,
      }}
      isDragging={isDragging}
      // @ts-expect-error TS2322
      ref={ref}
    >
      <Link
        greyLink
        textProps={{
          fontSize: 'small', marginTop: isFirstItem ? '28px' : '10px', marginRight: 'large', width: 'auto',
        }}
      >
        <FontAwesomeIcon style={{ marginRight: '4px' }} icon={faBars} />
      </Link>

      <Form.Field
        boxProps={{
          flexShrink: 1,
          opacity: hidden ? 0.3 : 1,
          marginRight: 'large',
          minWidth: '150px',
          width: '100%',
        }}
        labelProps={{
          opacity: isDragging ? 0 : 1, // Set opacity to 0 when dragging
        }}
        label={isFirstItem ? '* Title' : ''}
      >
        <input
          disabled={!isMaster}
          name="title"
          onBlur={() => updateFn(entityState)}
          onChange={handlers.onChange}
          type="text"
          value={title}
        />
      </Form.Field>

      <Box width="auto">
        <Form.Field
          boxProps={{
            flexShrink: 1,
            marginRight: 'large',
            opacity: hidden ? 0.3 : 1,
            width: '120px',
          }}
          labelProps={{
            opacity: isDragging ? 0 : 1,
          }}
          label={isFirstItem ? 'Product Code' : ''}

        >
          <input
            disabled={!isMaster}
            name="product_code"
            onBlur={() => updateFn(entityState)}
            onChange={handlers.onChange}
            type="text"
            value={product_code}
          />
        </Form.Field>

        <Form.Field
          boxProps={{
            flexShrink: 1,
            marginRight: 'large',
            opacity: hidden ? 0.3 : 1,
            width: '120px',
          }}
          labelProps={{
            opacity: isDragging ? 0 : 1,
          }}
          label={priceTextFormatted({ isFirstItem, isMaster })}
        >
          <input
            disabled={hidden}
            name="price"
            style={{ width: 120 }}
            onBlur={() => updateFn(entityState)}
            onChange={handlers.onChange}
            type="number"
            value={price}
          />
        </Form.Field>
      </Box>

      <Box width="auto" marginTop="auto">
        <Form.Field
          boxProps={{
            width: 'auto',
          }}
          labelProps={{
            opacity: isDragging ? 0 : 1,
          }}
          label={`${isFirstItem ? 'Default' : ''}`}
        >
          <Button
            buttonStyle="secondaryEdit"
            icon={(
              <FontAwesomeIcon
                style={{ color: isDefault ? 'green' : 'red' }}
                icon={isDefault ? faToggleOn : faToggleOff}
              />
            )}
            onClick={() => toggleDefaultFn()}
            // @ts-expect-error
            marginRight="large"
            size="large"
            style={{ height: 37 }}
            width={42}
          />
        </Form.Field>

        <Form.Field
          boxProps={{
            width: 'auto',
            marginTop: isMaster && isFirstItem ? '12px' : 'none',
          }}
          labelProps={{
            opacity: isDragging ? 0 : 1,
          }}
          label={DefaultFormatted({ isFirstItem, isMaster })}
        >
          <Button
            buttonStyle="secondaryDestroy"
            icon={<DeleteIcon hidden={hidden} isMaster={isMaster} />}
            onClick={isMaster ? () => confirmDelete(confirmDeletePayload) : () => toggleVisibilityFn()}
            size="large"
            style={{ height: 37 }}
            // @ts-expect-error TS2322
            width={42}
          />
        </Form.Field>
      </Box>
    </ListItem>
  )
})

export default DraggableProviderPriceListOptionValue
